import { FC, useCallback, useRef, useState } from 'react';

import type { SanityTeamInputBlock, SanityQuizTheme } from '@lib/sanity/types';

type InputMode = 'upload' | 'text';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpeg'];

type TeamInputBlockProps = SanityTeamInputBlock & {
  theme?: SanityQuizTheme;
  onSubmit: (input: { mode: InputMode; value: string; file?: File }) => void;
};

const UploadIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const TeamInputBlock: FC<TeamInputBlockProps> = ({
  heading,
  subheading,
  uploadLabel,
  textInputLabel,
  uploadHelpText,
  uploadDropzoneText,
  textInputPlaceholder,
  submitButtonText,
  onSubmit
}) => {
  const [inputMode, setInputMode] = useState<InputMode>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [teamName, setTeamName] = useState('');
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((file: File) => {
    setFileError(null);

    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      setFileError('Please upload a PNG or JPG image.');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError('File must be under 10MB.');
      return;
    }

    setSelectedFile(file);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragOver(false);
      const file = event.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const isSubmitDisabled =
    inputMode === 'upload' ? !selectedFile : !teamName.trim();

  const handleSubmitClick = () => {
    if (isSubmitDisabled) return;

    if (inputMode === 'upload' && selectedFile) {
      onSubmit({
        mode: 'upload',
        value: selectedFile.name,
        file: selectedFile
      });
    } else {
      onSubmit({ mode: 'text', value: teamName.trim() });
    }
  };

  return (
    <section className="flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-sm text-center">
        <h2
          className="mb-2 text-center font-heading font-black text-neutral-800"
          style={{
            fontSize: 'clamp(1.5rem, 4vw, 2rem)',
            lineHeight: 1.15
          }}
        >
          {heading}
        </h2>
        {subheading && (
          <p className="mb-8 text-sm text-neutral-500 font-body">
            {subheading}
          </p>
        )}

        {/* Mode toggle */}
        <div className="mb-6 inline-flex overflow-hidden rounded border-2 border-neutral-300">
          <button
            type="button"
            onClick={() => setInputMode('upload')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold font-body transition-all ${
              inputMode === 'upload'
                ? 'bg-neutral-800 text-white'
                : 'bg-transparent text-neutral-600'
            }`}
          >
            <UploadIcon />
            {uploadLabel}
          </button>
          <button
            type="button"
            onClick={() => setInputMode('text')}
            className={`px-6 py-3 text-sm font-semibold font-body transition-all ${
              inputMode === 'text'
                ? 'bg-neutral-800 text-white'
                : 'bg-transparent text-neutral-600'
            }`}
          >
            {textInputLabel}
          </button>
        </div>

        {inputMode === 'upload' ? (
          <div>
            <div
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`mb-4 flex cursor-pointer flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed px-6 py-10 transition-all duration-200 ${
                isDragOver
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-neutral-300 bg-white'
              }`}
            >
              <div className="text-neutral-400">
                <UploadIcon />
              </div>
              <p className="text-sm font-semibold text-neutral-700 font-body">
                {selectedFile ? selectedFile.name : uploadDropzoneText}
              </p>
              <p className="text-xs text-neutral-400 font-body">
                {uploadHelpText}
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg"
              onChange={handleFileInputChange}
              className="hidden"
            />
            {fileError && (
              <p className="mb-4 text-sm text-primary-600 font-body">
                {fileError}
              </p>
            )}
          </div>
        ) : (
          <input
            type="text"
            value={teamName}
            onChange={(event) => setTeamName(event.target.value)}
            placeholder={textInputPlaceholder}
            className="mb-4 w-full rounded-md border-2 border-neutral-300 bg-white px-[18px] py-3.5 text-sm text-neutral-800 font-body outline-none transition-all focus:border-primary-600"
          />
        )}

        <button
          type="button"
          onClick={handleSubmitClick}
          disabled={isSubmitDisabled}
          className={`mt-2 w-full rounded px-8 py-4 text-sm font-semibold tracking-wide font-body transition-all duration-300 ${
            isSubmitDisabled
              ? 'cursor-not-allowed bg-neutral-300 text-neutral-500'
              : 'cursor-pointer bg-primary-600 text-white'
          }`}
          style={{
            boxShadow: isSubmitDisabled
              ? 'none'
              : '0 4px 16px rgba(0, 157, 229, 0.3)'
          }}
        >
          {submitButtonText}
        </button>
      </div>
    </section>
  );
};

export default TeamInputBlock;
